/* 
  AccessPolicy.ts
  - Defines the allowlist of enforced backend limitation keys
  - Compiles raw backend payload into a typed, ergonomic Policy
  - Deny-by-default and resilient to unknown/malformed values
*/

export type RawAccess = Record<string, any>;

// Enforced keys (first batch). Expand this set to enable new flags progressively.
export const ENFORCED_ACCESS_KEYS = new Set<string>([
  "timetable_last_days_count",
  "timetable_phones_access",
  "timetable_staff_id",
  "records_access",
  "records_create_access",
  // writing consumables off on a visit (boona storage plan ST1: owner/admin, not "worker")
  "records_consumables_edit_access",
  // dashboard visibility
  "dashboard_access",
  // sidebar link visibility
  "settings_staff_access",
  "settings_services_access",
  "storages_access",
  "storages_goods_access",
  "settings_services_edit_technological_card_access",
  "finances_access",
  "settings_records_access",
  "clients_access",
  "settings_access",
  "dashboard_reviews_access",
]);

export type Policy = {
  records: {
    read: boolean;   // can open booking details modal
    create: boolean; // can open create booking modal
    writeOff: boolean; // sees the visit's "Write-off consumables" section
  };
  timetable: {
    phonesVisible: boolean;        // show phone on cards & in modal
    lastDaysCount: number | -1;    // -1=infinite, 0=future only, N>0=past N days
    forcedStaffId?: number;        // lock calendar to this staff id
  };
  // dashboard-level permissions
  dashboard: {
    access: boolean;               // controls visibility of the sidebar links block
  };
  // notifications panel access
  notifications: {
    access: boolean;               // can view notifications panel (derived: true if NOT staff)
  };
  // sidebar link visibility (per-link RBAC)
  sidebar: {
    staff: boolean;       // /dashboard/staff
    services: boolean;    // /dashboard/services
    storage: boolean;     // /dashboard/storage
    goods: boolean;       // /dashboard/goods
    techCards: boolean;   // /dashboard/tech-cards
    financials: boolean;  // /dashboard/financials
    records: boolean;     // /dashboard/records
    clients: boolean;     // /dashboard/clients
    settings: boolean;    // /dashboard/settings
    reviews: boolean;     // /dashboard/reviews
  };
};

// Compile raw backend payload into a typed Policy using the allowlist.
// Unknown keys are ignored; missing/malformed values fall back to safe defaults.
export function compilePolicy(raw: RawAccess | null | undefined): Policy {
  const src = raw ?? {};

  // Pick only allowlisted values into a local object for type checks.
  const pick = (k: string) => (ENFORCED_ACCESS_KEYS.has(k) ? (src as any)[k] : undefined);

  // Coerce booleans after type checks
  const recordsAccess = !!pick("records_access");
  const recordsCreateAccess = !!pick("records_create_access");
  const recordsWriteOff = !!pick("records_consumables_edit_access");
  const phonesAccess = !!pick("timetable_phones_access");
  const dashboardAccess = !!pick("dashboard_access");

  // Sidebar link visibility
  const sidebarStaff = !!pick("settings_staff_access");
  const sidebarServices = !!pick("settings_services_access");
  const sidebarStorage = !!pick("storages_access");
  const sidebarGoods = !!pick("storages_goods_access");
  const sidebarTechCards = !!pick("settings_services_edit_technological_card_access");
  const sidebarFinancials = !!pick("finances_access");
  const sidebarRecords = !!pick("settings_records_access");
  const sidebarClients = !!pick("clients_access");
  const sidebarSettings = !!pick("settings_access");
  // the reviews pages are guarded by settings_records_access (dashboard_reviews_access guards nothing)
  const sidebarReviews = !!pick("settings_records_access");

  // Numbers: validate and clamp semantics
  const rawDays = pick("timetable_last_days_count");
  const lastDaysCount: number | -1 =
    typeof rawDays === "number" ? (rawDays as number) : 0;

  const rawStaff = pick("timetable_staff_id");
  const forcedStaffId: number | undefined =
    typeof rawStaff === "number" ? (rawStaff as number) : undefined;

  // Notifications: currently derived from staff status (no forcedStaffId = admin)
  // Later can be replaced with a dedicated backend key
  const notificationsAccess = forcedStaffId === undefined;

  return {
    records: {
      read: recordsAccess,
      create: recordsCreateAccess,
      writeOff: recordsWriteOff,
    },
    timetable: {
      phonesVisible: phonesAccess,
      lastDaysCount,
      forcedStaffId,
    },
    dashboard: {
      access: dashboardAccess,
    },
    notifications: {
      access: notificationsAccess,
    },
    sidebar: {
      staff: sidebarStaff,
      services: sidebarServices,
      storage: sidebarStorage,
      goods: sidebarGoods,
      techCards: sidebarTechCards,
      financials: sidebarFinancials,
      records: sidebarRecords,
      clients: sidebarClients,
      settings: sidebarSettings,
      reviews: sidebarReviews,
    },
  };
}
